import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import createAuthStore from "./useAuthStore";
import createSpaceStore from "./useSpaceStore";
import createUtilStore from "./useUtilStore";
import createOnboardingStore from "./useOnboardingStore";
import { createHistoryStore } from "./useHistoryStore";
import { supabase } from "@/utils/supabase/client";

const initialState = {
  past: [],
  future: [],
  actions: [],
  currentIndex: -1,
  isMaintenanceMode: false,
  isMaintenanceModeLoading: true,
  debugModeEnabled: false,
};

// Create the store with conditional persistence
const createStore = () => {
  const storeConfig = (set, get) => ({
    ...initialState,
    ...createUtilStore(set, get),
    ...createAuthStore(set, get),
    ...createSpaceStore(set, get),
    ...createOnboardingStore(set, get),
    ...createHistoryStore(set, get),
    setDebugModeEnabled: (enabled) => set({ debugModeEnabled: enabled }),
    checkMaintenanceMode: async () => {
      try {
        const { data, error } = await supabase
          .from("system_utils")
          .select("*")
          .single();

        if (error) {
          console.error("Error fetching maintenance mode:", error);
          set({ isMaintenanceMode: false, isMaintenanceModeLoading: false });
          return false;
        }

        set({
          isMaintenanceMode: data?.maintenance_mode || false,
          isMaintenanceModeLoading: false,
        });
        return data?.maintenance_mode || false;
      } catch (error) {
        console.error("Error checking maintenance mode:", error);
        set({ isMaintenanceMode: false, isMaintenanceModeLoading: false });
        return false;
      }
    },
  });

  // Only use persistence on client side
  if (typeof window !== "undefined") {
    return create(
      persist(storeConfig, {
        name: "qensa-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          // Only persist essential auth data
          session: state.session,
          cachedUserSettings: state.cachedUserSettings,
          lastSessionCheck: state.lastSessionCheck,
          debugModeEnabled: state.debugModeEnabled,
        }),
        onRehydrateStorage: () => {
          console.log("🔄 [Store] Rehydrating persisted state");
          return (state, error) => {
            if (error) {
              console.error("❌ [Store] Rehydration failed:", error);
            } else {
              console.log("✅ [Store] State rehydrated successfully");
              // Reset loading states after rehydration
              if (state) {
                state.loading = false;
                state.error = null;
                state.isInitialized = false; // Force re-initialization
              }
            }
          };
        },
      })
    );
  } else {
    // Server-side: return a simple store without persistence
    return create(storeConfig);
  }
};

const store = createStore();

// Helper to safely use store with consistent server/client state
const useStore = (selector) => {
  if (typeof window === "undefined") {
    // On server, return initial state to avoid hydration mismatches
    return selector ? selector(initialState) : initialState;
  }

  return store(selector);
};

// For direct store access
useStore.getState = () => {
  if (typeof window === "undefined") {
    return initialState;
  }
  return store.getState();
};

useStore.setState = (update) => {
  if (typeof window !== "undefined") {
    return store.setState(update);
  }
};

export default useStore;
